import { ApiError } from '@brush/api-client';
import { useActingRole, type ActingRole } from '@brush/auth';
import { AccessDenied, ErrorRetry, RoleChooser, Spinner, type RoleChoice } from '@brush/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Overview } from './components/Overview';
import { RoomShell } from './components/RoomShell';
import { ContestsView } from './components/fantasy/ContestsView';
import { PlayersView } from './components/players/PlayersView';
import { SettingsView } from './components/settings/SettingsView';
import { LeaderboardsView } from './components/leaderboards/LeaderboardsView';
import { RatingsView } from './components/ratings/RatingsView';
import { TournamentsView } from './components/tournaments/TournamentsView';
import {
  SETTINGS_PANES,
  changeDestination,
  templatesDestination,
  sectionDestination,
  settingsPaneAfter,
  type Destination,
  type PeriodFocus,
  type RoomView,
  type SettingsPane,
} from './nav';
import { useScreenViewed, useSession, useSessionStarted } from './api';
import { useCurrentUser, useRoomSummary } from './queries';
import { summaryRoomFor } from './summary';
import { useRoom } from './room';
import { roleChoicesFor, STALE_ROLE_NOTICE, UNASSIGNED_OPERATOR_NOTICE } from './roles';
import { useUnsaved } from './unsaved';

const centered = 'grid min-h-screen place-items-center bg-felt-50/40 p-6';

export function App() {
  const me = useCurrentUser();
  const { displayName, signOut } = useSession();
  useSessionStarted(me.data !== undefined);
  const { room, roomId, isLoading, isError, refetch } = useRoom();
  const [view, setView] = useState<RoomView>('overview');
  // Which Settings pane is on screen, held here rather than in Settings so a link elsewhere can open one (A6).
  const [settingsPane, setSettingsPane] = useState<SettingsPane>(SETTINGS_PANES[0]!.pane);
  // A Change link's request to show the Periods card and focus a field (A6b); null for every other way in.
  const [periodFocus, setPeriodFocus] = useState<PeriodFocus | null>(null);
  // Settings is a frame, not a screen: its panes report themselves (SettingsView).
  useScreenViewed(room && view !== 'settings' ? view : null, room?.id ?? null);
  const { leave } = useUnsaved();
  const { actingRole, choose, clear } = useActingRole();
  const queryClient = useQueryClient();
  const [choiceWasRefused, setChoiceWasRefused] = useState(false);

  // A stored choice the server has stopped accepting — an assignment withdrawn between sessions.
  const refusedChoice = me.error instanceof ApiError && me.error.code === 'acting_role_not_held';

  // Clear it rather than re-offering a list guessed at from nothing: with the choice gone the
  // query key changes, /api/me is asked again with no header, and the roles this account may
  // still act as come back from the server instead of from an assumption here. The flag is what
  // survives that round trip, so the chooser can say why it is asking again — otherwise it reads
  // as the app having forgotten who you are, which sends somebody off to sign in again for
  // nothing.
  useEffect(() => {
    if (!refusedChoice) return;
    setChoiceWasRefused(true);
    clear();
  }, [refusedChoice, clear]);

  const pickRole = (id: string) => {
    setChoiceWasRefused(false);
    choose(id as ActingRole);
    // Everything cached was read as somebody else. Clearing is the honest reset: an invalidation
    // would refetch each query but leave the previous role's answers on screen meanwhile, which
    // for a narrowed role means showing rooms the caller can no longer write to.
    queryClient.clear();
  };

  const chooser = (notice: string | null, choices: RoleChoice[]) => (
    <RoleChooser
      appName="Brush Room"
      choices={choices}
      onChoose={pickRole}
      notice={notice}
      currentId={actingRole}
      accountName={me.data?.name ?? displayName}
      onSignOut={signOut}
    />
  );

  // Changing section unmounts whatever is on screen, so a section button is a way out of an
  // editor as much as the editor's own back button is. The Change link beside the Rundown's and
  // Overview's period goes the same way, to Settings → Preferences with the Periods card in view and
  // the matching field focused (UP21, UP24, A6, A6b): no router, the section, the Settings pane and the
  // field asked for are this component's state.
  const go = (to: Destination) =>
    leave(() => {
      setSettingsPane(settingsPaneAfter({ view, pane: settingsPane }, to));
      setPeriodFocus(to.field ? { field: to.field, seq: Date.now() } : null);
      setView(to.view);
    });
  const navigate = (next: RoomView) => go(sectionDestination(next));

  // The navigation's badges, from the room summary the shell reads anyway (performance rules 3 and 4).
  // They were five whole lists fetched to take `.length` — the tournament list alone 1.9 MB on SHRPO.
  // Not until a role is in force: behind the chooser it would be read as nobody, then again (known gap 70).
  const offerable = me.data ? roleChoicesFor(me.data.availableRoles).length : null;
  const summary = useRoomSummary(summaryRoomFor(roomId, offerable, actingRole));

  // Entitlement first: this app is for platform administrators and room operators.
  if (me.isLoading) {
    return (
      <div className={centered}>
        <Spinner label="Checking access…" />
      </div>
    );
  }
  // The effect above is clearing the refused choice; what follows is the refetch it triggers.
  if (refusedChoice) {
    return (
      <div className={centered}>
        <Spinner label="Checking access…" />
      </div>
    );
  }
  if (me.isError || !me.data) {
    return (
      <div className={centered}>
        <ErrorRetry message="Couldn't verify your access." onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (!me.data.isAdmin && !me.data.isRoomOperator) {
    return (
      <AccessDenied
        appName="Brush Room"
        message="This app is for room operators and platform administrators. Your account has neither role — ask a platform administrator for access."
        accountName={me.data.name ?? displayName}
        onSignOut={signOut}
      />
    );
  }
  const choices = roleChoicesFor(me.data.availableRoles);
  const canSwitch = choices.length > 1;

  // Only when there is a genuine choice to make. One role is not a decision, and an account with
  // none was refused above — the chooser must not stand between somebody and the sentence
  // explaining why they cannot be here.
  if (canSwitch && actingRole === null) {
    return chooser(choiceWasRefused ? STALE_ROLE_NOTICE : null, choices);
  }

  if (me.data.isRoomOperator && !me.data.isAdmin && me.data.roomIds.length === 0) {
    // An administrator who chose to act as an operator and operates nothing lands here, and for
    // them it is not a dead end: the way out is the role they set aside, so they are offered it
    // rather than told to ask an administrator — which they are. Somebody who really holds only
    // the room role has nothing to switch to and gets the sentence unchanged.
    return canSwitch
      ? chooser(UNASSIGNED_OPERATOR_NOTICE, choices)
      : (
          <AccessDenied
            appName="Brush Room"
            message="Your account is a room operator but isn't assigned to any room yet. A platform administrator needs to assign you before you can manage one."
            accountName={me.data.name ?? displayName}
            onSignOut={signOut}
          />
        );
  }

  if (isLoading) {
    return (
      <div className={centered}>
        <Spinner label="Loading room…" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className={centered}>
        <ErrorRetry message="Couldn't load rooms from the API." onRetry={refetch} />
      </div>
    );
  }
  if (!room) {
    return (
      <div className={centered}>
        <p className="max-w-sm text-center text-sm text-ink-800/60">
          {me.data.isAdmin
            ? 'No rooms yet. Register one in the admin app, then reload.'
            : "You aren't assigned to any room yet."}
        </p>
      </div>
    );
  }

  const tally = summary.data?.counts;
  const counts: Partial<Record<RoomView, number>> = {
    // The true total — the list it replaced stopped at 200 (known gap 6).
    tournaments: tally?.tournaments,
    leaderboards: tally?.leaderboards,
    // THE ROOM'S OWN BOARDS, which the server counts: never the platform's templates beside them
    // (§2.3), which would read on an empty room as ratings that exist and cannot be found.
    ratings: tally?.ratingBoards,
    fantasy: tally?.fantasyContests,
    players: tally?.players,
  };

  return (
    <RoomShell view={view} onNavigate={navigate} onChangePeriods={() => go(changeDestination('rail'))} counts={counts}>
      {view === 'overview' ? (
        <Overview onNavigate={navigate} onChangePeriods={() => go(changeDestination('overview'))} />
      ) : view === 'tournaments' ? (
        <TournamentsView />
      ) : view === 'leaderboards' ? (
        <LeaderboardsView />
      ) : view === 'ratings' ? (
        <RatingsView />
      ) : view === 'fantasy' ? (
        <ContestsView />
      ) : view === 'players' ? (
        <PlayersView onOpenTemplates={() => go(templatesDestination())} />
      ) : (
        <SettingsView
          user={me.data}
          pane={settingsPane}
          focus={periodFocus}
          onSelectPane={(next) => {
            setSettingsPane(next);
            setPeriodFocus(null);
          }}
        />
      )}
    </RoomShell>
  );
}
